import React from "react";
import * as L from "lucide-react";

/** Weather, rendered properly instead of the model retyping numbers into prose.
 *  Payload comes from the backend `weather` SSE event, which is fed by the
 *  structured provider result — so what's on screen is what was fetched. */
export function WeatherCard({ card, testid = "weather-card" }) {
  if (!card || card.temp_c === null || card.temp_c === undefined) return null;
  const { city, temp_c, feels_c, condition, humidity, wind_kmh,
          high_c, low_c, precip_pct, provider_label } = card;
  const Icon = iconFor(condition, precip_pct);

  return (
    <div
      className="mt-1 mb-1.5 w-[320px] max-w-full rounded-3xl overflow-hidden border border-black/[0.06] shadow-[0_10px_40px_rgba(17,19,24,0.06)]"
      data-testid={testid}
    >
      <div className="relative p-5" style={{ background: gradientFor(condition, precip_pct) }}>
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 85% 15%, rgba(255,255,255,.9), transparent 45%)" }}
        />
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/80 font-semibold truncate"
              data-testid={`${testid}-city`}>
              {city}
            </div>
            <div className="flex items-baseline gap-1.5 mt-1.5">
              <span className="text-[44px] leading-none font-display text-white" data-testid={`${testid}-temp`}>
                {Math.round(temp_c)}
              </span>
              <span className="text-lg text-white/85 font-display">°C</span>
            </div>
            {condition && (
              <div className="text-[13px] text-white/90 mt-1 first-letter:uppercase" data-testid={`${testid}-condition`}>
                {condition}
                {feels_c !== null && feels_c !== undefined && (
                  <span className="text-white/70"> · feels {Math.round(feels_c)}°</span>
                )}
              </div>
            )}
          </div>
          <Icon size={40} strokeWidth={1.5} className="text-white/90 shrink-0"/>
        </div>
      </div>

      <div className="bg-[var(--rm-surface,#fff)] px-5 py-3.5">
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat label="High / low" value={
            high_c !== null && high_c !== undefined ? `${high_c}° / ${low_c}°` : "—"
          } testid={`${testid}-range`}/>
          <Stat label="Rain" value={precip_pct !== null && precip_pct !== undefined ? `${precip_pct}%` : "—"}
            testid={`${testid}-rain`}/>
          <Stat label="Wind" value={wind_kmh !== null && wind_kmh !== undefined ? `${Math.round(wind_kmh)} km/h` : "—"}
            testid={`${testid}-wind`}/>
        </div>
        <div className="mt-3 pt-2.5 border-t border-black/[0.06] flex items-center justify-between text-[10px] text-[var(--rm-text-faint,#98928a)]">
          <span>{humidity !== null && humidity !== undefined ? `Humidity ${Math.round(humidity)}%` : ""}</span>
          <span data-testid={`${testid}-provider`}>{provider_label}</span>
        </div>
      </div>

      {Array.isArray(card.days) && card.days.length > 1 && (
        <div className="bg-[var(--rm-surface,#fff)] px-3 pb-4" data-testid={`${testid}-days`}>
          <div className="grid grid-cols-3 gap-2">
            {card.days.slice(0, 3).map((d, i) => {
              const DayIcon = iconFor(d.condition, d.precip_pct);
              return (
                <div
                  key={d.date || i}
                  className="rounded-2xl bg-black/[0.03] px-2 py-2.5 text-center"
                  data-testid={`${testid}-day-${i}`}
                  title={d.condition || ""}
                >
                  <div className="text-[10px] uppercase tracking-[0.1em] text-[var(--rm-text-faint,#98928a)]">
                    {dayLabel(d.date, i)}
                  </div>
                  <DayIcon size={18} strokeWidth={1.6} className="mx-auto my-1.5 text-[var(--rm-text-muted,#6b6560)]"/>
                  <div className="text-[13px] font-medium leading-none">
                    {d.high_c}°<span className="text-[var(--rm-text-faint,#98928a)]"> / {d.low_c}°</span>
                  </div>
                  {d.precip_pct !== null && d.precip_pct !== undefined && (
                    <div className="text-[10px] text-[var(--rm-text-faint,#98928a)] mt-1">{d.precip_pct}% rain</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-[var(--rm-text-faint,#98928a)]">{label}</div>
      <div className="text-[14px] font-medium mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}

function dayLabel(iso, index) {
  if (index === 0) return "Today";
  try {
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return "—";
    if (index === 1) return "Tomorrow";
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch (_) {
    return "—";
  }
}

function iconFor(condition, pop) {
  const c = (condition || "").toLowerCase();
  if (c.includes("thunder")) return L.CloudLightning;
  if (c.includes("snow") || c.includes("sleet")) return L.CloudSnow;
  if (c.includes("drizzle")) return L.CloudDrizzle;
  if (c.includes("rain") || c.includes("shower")) return L.CloudRain;
  if (c.includes("fog")) return L.CloudFog;
  if (c.includes("overcast") || c.includes("cloudy")) return L.Cloud;
  if (c.includes("clear") || c.includes("fair")) return L.Sun;
  return (pop ?? 0) > 50 ? L.CloudRain : L.CloudSun;
}

function gradientFor(condition, pop) {
  const c = (condition || "").toLowerCase();
  if (c.includes("thunder")) return "linear-gradient(135deg,#2b2d42 0%,#4a4e69 55%,#6b5b95 100%)";
  if (c.includes("snow") || c.includes("sleet")) return "linear-gradient(135deg,#5c7ea8 0%,#8fb0cc 60%,#c9d9e6 100%)";
  if (c.includes("rain") || c.includes("drizzle") || c.includes("shower"))
    return "linear-gradient(135deg,#31506b 0%,#4c7392 55%,#7ba0b8 100%)";
  if (c.includes("fog")) return "linear-gradient(135deg,#6b7280 0%,#9aa2ab 60%,#c4c9ce 100%)";
  if (c.includes("overcast") || c.includes("cloudy"))
    return "linear-gradient(135deg,#4a5a6b 0%,#7a8b9c 60%,#a9b6c2 100%)";
  if (c.includes("clear") || c.includes("fair"))
    return "linear-gradient(135deg,#1f7a8c 0%,#2a9d8f 45%,#e9c46a 120%)";
  return (pop ?? 0) > 50
    ? "linear-gradient(135deg,#31506b 0%,#4c7392 55%,#7ba0b8 100%)"
    : "linear-gradient(135deg,#2a6f7f 0%,#3d9aa1 55%,#8ecfc9 100%)";
}

export default WeatherCard;
